import * as React from "react"

import { cva } from "class-variance-authority"
import { Minus, Plus } from "lucide-react"

import { cn } from "../../cn"
import { Tooltip } from "../Tooltip/Tooltip"

const numberInputWrapperVariants = cva([
  "coval-field flex items-center gap-0.5",
  "h-control w-full rounded-md pl-2 pr-0.5",
  "bg-surface-default",
  "transition-colors duration-150",
  "cursor-text",
  "has-[input:disabled]:cursor-default has-[input:read-only]:cursor-default",
  "border border-surface-1",
  // Keyed to the field, not to any disabled child: a stepper disabled at a
  // bound must not fade the value beside it.
  "has-[input:disabled]:opacity-50",
])

const numberInputStepperVariants = cva([
  // A 24px hit area meets WCAG 2.5.8. It is set in px because the root font
  // size scales rem steps below 24px.
  "inline-flex size-[24px] shrink-0 cursor-pointer items-center justify-center rounded-sm",
  "text-coval-text-muted transition-colors duration-150",
  "hover:bg-surface-default-hover hover:text-coval-text",
  "disabled:cursor-default disabled:opacity-50",
  "disabled:hover:bg-transparent disabled:hover:text-coval-text-muted",
])

// Each digit run has one way to match, so a long entry cannot backtrack.
const NUMBER_PATTERN = /^[+-]?(\d+(\.\d*)?|\.\d+)(e[+-]?\d+)?$/i

/**
 * The number a draft spells, `undefined` for an empty draft, `null` for a
 * partial or unreadable one. A comma is not read as a decimal separator:
 * "1,000" and "1,5" mean different things in different locales.
 */
function parseDraft(draft: string): number | undefined | null {
  const trimmed = draft.trim()
  if (trimmed === "") {
    return undefined
  }
  if (!NUMBER_PATTERN.test(trimmed)) {
    return null
  }
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

function formatValue(value: number | null | undefined): string {
  return value != null && Number.isFinite(value) ? String(value) : ""
}

function decimalPlaces(value: number): number {
  const [mantissa, exponent] = String(value).toLowerCase().split("e")
  const fraction = mantissa.split(".")[1]?.length ?? 0
  return Math.max(0, fraction - Number(exponent ?? 0))
}

function clamp(value: number, min?: number, max?: number): number {
  if (min != null && value < min) {
    return min
  }
  if (max != null && value > max) {
    return max
  }
  return value
}

export interface NumberInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  | "value"
  | "defaultValue"
  | "onChange"
  | "type"
  | "min"
  | "max"
  | "step"
  | "size"
> {
  /** The current number. `null` and `undefined` render an empty field. */
  value: number | null | undefined
  /**
   * Called with each number the user types or steps to, and with `undefined`
   * when they empty the field. A partial entry such as "-" calls nothing.
   */
  onValueChange: (value: number | undefined) => void
  min?: number
  max?: number
  /** Amount the arrow keys and steppers add or remove. Defaults to 1. */
  step?: number
  /** Amount Page Up and Page Down add or remove. Defaults to ten steps. */
  largeStep?: number
  /** Hides the increase and decrease buttons; the keyboard still steps. */
  hideStepper?: boolean
  error?: boolean
  /** Accessible name and tooltip of the increase button. */
  incrementLabel?: string
  /** Accessible name and tooltip of the decrease button. */
  decrementLabel?: string
}

/**
 * A numeric field exposed as an APG spinbutton. Arrow Up and Down step by
 * `step`, Page Up and Page Down by `largeStep`, and Home and End jump to `min`
 * and `max` when they are set. Stepping starts from the number the field
 * shows, or from zero when it is empty, and clamps to the bounds. A typed number
 * is passed through unchanged, and one outside the bounds is clamped when the
 * field loses focus. A value the user never edits is never rewritten, so a
 * stored value outside the bounds or with more precision than `step` survives.
 */
const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      value,
      onValueChange,
      min,
      max,
      step = 1,
      largeStep,
      hideStepper = false,
      error,
      incrementLabel = "Increase",
      decrementLabel = "Decrease",
      className,
      disabled,
      readOnly,
      id,
      onKeyDown,
      onBlur,
      "aria-invalid": ariaInvalid,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId()
    const inputId = id ?? generatedId
    const innerRef = React.useRef<HTMLInputElement | null>(null)
    const editedRef = React.useRef(false)

    const [draft, setDraft] = React.useState(() => formatValue(value))
    const [syncedValue, setSyncedValue] = React.useState(value)
    // A value from outside replaces the draft unless the draft already spells
    // it, so "1." stays on screen while the parent holds 1.
    if (!Object.is(syncedValue, value)) {
      setSyncedValue(value)
      const drafted = parseDraft(draft)
      if (drafted === null || drafted !== (value ?? undefined)) {
        setDraft(formatValue(value))
      }
    }

    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        innerRef.current = node
        if (typeof ref === "function") {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      },
      [ref],
    )

    const current = value != null && Number.isFinite(value) ? value : undefined
    // The spinbutton reports what it shows, which can differ from `value`
    // while a typed entry is pending or the parent keeps a stored value.
    const drafted = parseDraft(draft)
    const shownValue = typeof drafted === "number" ? drafted : undefined
    // An entry that spells no number is flagged rather than dropped silently;
    // it reverts to `value` when the field loses focus.
    const invalid = error || drafted === null
    const interactive = !disabled && !readOnly

    const commit = (next: number) => {
      setDraft(formatValue(next))
      onValueChange(next)
    }

    // Steps from what the field shows, so a pending typed entry is the base.
    // An empty field steps from zero, and an entry that spells no number
    // steps from the value it would revert to.
    const stepBy = (delta: number) => {
      const base = drafted === undefined ? 0 : (drafted ?? current ?? 0)
      const places = Math.max(decimalPlaces(base), decimalPlaces(delta))
      const stepped = Number((base + delta).toFixed(Math.min(places, 20)))
      commit(clamp(stepped, min, max))
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event)
      if (event.defaultPrevented || !interactive) {
        return
      }
      const big = largeStep ?? step * 10
      switch (event.key) {
        case "ArrowUp":
          stepBy(step)
          break
        case "ArrowDown":
          stepBy(-step)
          break
        case "PageUp":
          stepBy(big)
          break
        case "PageDown":
          stepBy(-big)
          break
        case "Home":
          if (min == null) {
            return
          }
          commit(min)
          break
        case "End":
          if (max == null) {
            return
          }
          commit(max)
          break
        default:
          return
      }
      event.preventDefault()
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      editedRef.current = true
      const nextDraft = event.target.value
      setDraft(nextDraft)
      const parsed = parseDraft(nextDraft)
      if (parsed !== null) {
        onValueChange(parsed)
      }
    }

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(event)
      const edited = editedRef.current
      editedRef.current = false
      const parsed = parseDraft(draft)
      if (edited && parsed != null) {
        const clamped = clamp(parsed, min, max)
        if (clamped !== parsed) {
          commit(clamped)
          return
        }
      }
      setDraft(formatValue(edited && parsed != null ? parsed : value))
    }

    const atMax = shownValue !== undefined && max != null && shownValue >= max
    const atMin = shownValue !== undefined && min != null && shownValue <= min

    const stepper = (
      label: string,
      delta: number,
      blocked: boolean,
      icon: React.ReactNode,
    ) => {
      const isDisabled = !interactive || blocked
      return (
        // A disabled stepper is left without a stand-in: the stand-in would
        // be a tab stop, and the field's value already says why it is off.
        <Tooltip content={label}>
          <button
            type="button"
            aria-label={label}
            aria-controls={inputId}
            // The field is the tab stop; the arrow keys do what these do.
            tabIndex={-1}
            disabled={isDisabled}
            // Keeps focus, and a pending typed entry, in the field.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => stepBy(delta)}
            className={numberInputStepperVariants()}
          >
            {icon}
          </button>
        </Tooltip>
      )
    }

    return (
      <div
        role="presentation"
        className={cn(
          numberInputWrapperVariants(),
          hideStepper && "pr-2",
          invalid &&
            "border-coval-danger-border focus-within:border-coval-danger-border bg-coval-danger-surface",
          className,
        )}
        onClick={(event) => {
          if ((event.target as HTMLElement).closest("button")) {
            return
          }
          innerRef.current?.focus()
        }}
      >
        <input
          ref={setRefs}
          id={inputId}
          type="text"
          inputMode="decimal"
          role="spinbutton"
          autoComplete="off"
          aria-valuenow={shownValue}
          aria-valuemin={min}
          aria-valuemax={max}
          // A FormField passes its own aria-invalid, which is undefined when
          // it has no error; `error` or an unreadable entry still marks it invalid.
          aria-invalid={invalid || ariaInvalid || undefined}
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={disabled}
          readOnly={readOnly}
          className={cn(
            "h-full w-full min-w-0 bg-transparent py-1.5 outline-none",
            "text-sm tabular-nums text-coval-text placeholder:text-coval-text-muted",
            invalid &&
              "text-coval-danger-on-surface placeholder:text-coval-danger-on-surface/80",
          )}
          {...props}
        />
        {!hideStepper && (
          <>
            {stepper(
              decrementLabel,
              -step,
              atMin,
              <Minus aria-hidden="true" className="size-3.5" />,
            )}
            {stepper(
              incrementLabel,
              step,
              atMax,
              <Plus aria-hidden="true" className="size-3.5" />,
            )}
          </>
        )}
      </div>
    )
  },
)

NumberInput.displayName = "NumberInput"

export { NumberInput }
